import type React from 'react';
import { Avatar } from '@/components/data-display/Avatar';
import { StatusDot } from '@/components/data-display/StatusDot';
import type { AgentWithCounts } from '@/lib/api';
import { initials } from '@/lib/format';
import './Sidebar.scss';

export type NavKey = 'gallery' | 'knowledge' | 'connection' | 'usage' | 'settings';

export interface SidebarProps {
  customerName: string;
  agents: AgentWithCounts[];
  /** Which nav item is highlighted. 'builder' highlights "All agents". */
  activeView: NavKey | 'builder' | 'chat';
  activeAgentId: string | null;
  connected: boolean;
  onNavigate: (key: NavKey) => void;
  onSelectAgent: (agentId: string) => void;
  onNewAgent: () => void;
  onSignOut: () => void;
}

const NAV: Array<{ key: NavKey; label: string; matches: string[] }> = [
  { key: 'gallery', label: 'All agents', matches: ['gallery', 'builder'] },
  { key: 'knowledge', label: 'Knowledge library', matches: ['knowledge'] },
  { key: 'connection', label: 'Locate2u connection', matches: ['connection'] },
  { key: 'usage', label: 'Analytics', matches: ['usage'] },
  { key: 'settings', label: 'Settings', matches: ['settings'] },
];

export const Sidebar: React.FC<SidebarProps> = ({
  customerName,
  agents,
  activeView,
  activeAgentId,
  connected,
  onNavigate,
  onSelectAgent,
  onNewAgent,
  onSignOut,
}) => (
  <nav className="sidebar" aria-label="Main navigation">
    <div className="sidebar__brand">
      <img src="/assets/l2u-logo-blue.png" alt="Locate2u" className="sidebar__logo" />
      <div className="sidebar__product">Intelligence</div>
    </div>

    <div className="sidebar__nav">
      {NAV.map((item) => {
        const active = item.matches.includes(activeView);
        return (
          <button
            key={item.key}
            type="button"
            className={`sidebar__nav-item${active ? ' sidebar__nav-item--active' : ''}`}
            onClick={() => onNavigate(item.key)}
          >
            <StatusDot tone={active ? 'blue' : 'idle'} />
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>

    <div className="sidebar__section-label">Agents</div>

    <div className="sidebar__agents">
      {agents.map((agent) => {
        const active = activeView === 'chat' && agent.id === activeAgentId;
        return (
          <button
            key={agent.id}
            type="button"
            className={`sidebar__agent${active ? ' sidebar__agent--active' : ''}`}
            onClick={() => onSelectAgent(agent.id)}
          >
            <Avatar name={agent.name} colour={agent.colour} size={26} />
            <span className="sidebar__agent-name">{agent.name}</span>
            <StatusDot tone="live" size={6} pulse={active} />
          </button>
        );
      })}

      <button type="button" className="sidebar__new-agent" onClick={onNewAgent}>
        <span className="sidebar__new-agent-icon">+</span>
        <span>New agent</span>
      </button>
    </div>

    <div className="sidebar__account">
      <div className="sidebar__account-avatar">{initials(customerName)}</div>
      <div className="sidebar__account-text">
        <div className="sidebar__account-name">{customerName}</div>
        <div className="sidebar__account-status">
          <StatusDot tone={connected ? 'live' : 'idle'} size={6} />
          {connected ? 'Connected' : 'Disconnected'}
        </div>
      </div>
      <button
        type="button"
        className="sidebar__sign-out"
        onClick={onSignOut}
        title="Sign out"
        aria-label="Sign out"
      >
        &#8594;
      </button>
    </div>
  </nav>
);

export default Sidebar;
